import type { RoomRate as RoomRateInput } from '@elara/domain/models/schemas/room-schemas';
import type { CancellationDeadline } from '@elara/domain/payment-schedule';

import { RoomRateModelSpec } from '@elara/domain/models/TransientModelSpecs';
import { resolveCancellationDeadline } from '@elara/domain/payment-schedule';

import { createLocalModel } from '#firebase/createLocalModel.js';
import {
  deriveMealCombinationId,
  formatCancellationDeadline,
  matchesMealCombination,
  normalizeBoolean,
  summarizeCancelPolicy,
} from '#lib/room-utils.js';

// Types
export type MealCombinationId = 'none' | 'breakfast' | 'breakfast-lunch' | 'breakfast-lunch-dinner';
export type RefundablePreference = 'refundable' | 'non-refundable';

/**
 * The stay a rate is being shown for. Needed to turn a relative cancellation deadline ("2 days before
 * arrival") into a date; it lives on the parent RoomOption's search, not on the rate, so `RoomOptionModel`
 * passes it down. A rate carrying Sabre daily rates can derive it itself.
 */
export interface RoomRateStay {
  checkInDate: string;
  checkOutDate: string;
}

export interface MealCombination {
  id: MealCombinationId;
  name: string;
  includes: { breakfast: boolean; lunch: boolean; dinner: boolean };
}

// Constants
export const MEAL_COMBINATIONS: MealCombination[] = [
  { id: 'none', name: 'No meals', includes: { breakfast: false, lunch: false, dinner: false } },
  { id: 'breakfast', name: 'Breakfast', includes: { breakfast: true, lunch: false, dinner: false } },
  { id: 'breakfast-lunch', name: 'Breakfast and Lunch', includes: { breakfast: true, lunch: true, dinner: false } },
  {
    id: 'breakfast-lunch-dinner',
    name: 'Breakfast, Lunch, and Dinner',
    includes: { breakfast: true, lunch: true, dinner: true },
  },
];

// Factory
export const RoomRateModelFactory = createLocalModel(RoomRateModelSpec, (rate, stay?: RoomRateStay) => {
  // The stay the rate is priced for: what the caller passed, else the span of Sabre's daily rates.
  const firstNight = rate.localPricing?.dailyRates?.[0];
  const lastNight = rate.localPricing?.dailyRates?.at(-1);
  const resolvedStay: RoomRateStay | undefined =
    stay ??
    (firstNight?.startDate && lastNight?.endDate
      ? { checkInDate: firstNight.startDate, checkOutDate: lastNight.endDate }
      : undefined);

  return {
    ...rate,

    // Enrichment's name first, then the meal-based label both suppliers set, then the
    // supplier's own rate name — so a rate is never nameless on a card or a checkout line.
    get name() {
      return rate.generated?.name ?? rate.displayName ?? rate.name;
    },

    // Meals data lives on the `meals` sub-object (Sabre-mapped); the
    // top-level `*Included` fields are deprecated legacy storage. Prefer the
    // sub-object so newly-fetched rates surface meals correctly.
    get breakfastIncluded() {
      return normalizeBoolean(rate.meals?.breakfastIncluded ?? rate.breakfastIncluded);
    },

    get lunchIncluded() {
      return normalizeBoolean(rate.meals?.lunchIncluded ?? rate.lunchIncluded);
    },

    get dinnerIncluded() {
      return normalizeBoolean(rate.meals?.dinnerIncluded ?? rate.dinnerIncluded);
    },

    /**
     * Whether the cancellation terms shown for this rate are the hotel's stated rule or only the
     * search's estimate. A Travelport rate carries the search's single tier, marked `source:
     * 'search'`, until Rules has been read for it (at search time for presented rates, otherwise on
     * card open, refresh or checkout); until then the card makes no cancellation claim, because the
     * search's tier was measured wrong on Marriott brands and IHG prepaid rates. Sabre rates carry
     * no source and are always confirmed.
     */
    get refundStatus(): 'refundable' | 'non-refundable' | 'unconfirmed' {
      const policies = rate.cancellationPolicies ?? [];
      const fromSearchOnly = policies.length > 0 && policies.every((p) => p.source === 'search');
      const travelportWithoutPolicy =
        policies.length === 0 && typeof rate.rateKey === 'string' && rate.rateKey.includes(':');
      if (fromSearchOnly || travelportWithoutPolicy) return 'unconfirmed';
      return normalizeBoolean(rate.refundable) ? 'refundable' : 'non-refundable';
    },

    get isRefundable(): boolean {
      return normalizeBoolean(rate.refundable);
    },

    /**
     * The headline cancellation fact: "Free cancellation until Sep 11, 2026" when the rate's structured
     * policy resolves a deadline against the stay, else "Free cancellation" / "Non-refundable" /
     * "Free-cancellation deadline passed (…)". THE single source for this label — every surface that shows
     * a rate's cancellation status reads it, so the wording can't drift between the room guide, the
     * itinerary and the checkout (it repeatedly did).
     */
    get cancellationPolicyTitle(): string {
      const resolved = formatCancellationDeadline(this.cancellationDeadline);
      if (this.refundStatus === 'unconfirmed') return 'Cancellation terms confirmed when you open the room';
      return resolved ?? (normalizeBoolean(rate.refundable) ? 'Refundable' : 'Non-refundable');
    },

    /**
     * Whether the free-cancellation deadline has gone: the "deadline passed" title, as a fact a card can
     * style by. A refundable rate past its deadline no longer cancels for free, so it must not read as
     * the good news a still-open one does.
     */
    get isCancellationDeadlinePassed(): boolean {
      const freeUntil = this.cancellationDeadline?.freeUntil;
      return this.cancellationDeadline?.refundable === true && !!freeUntil && freeUntil.getTime() < Date.now();
    },

    /** The rate's structured cancellation terms resolved against the stay; `undefined` without either. */
    get cancellationDeadline(): CancellationDeadline | undefined {
      if (!resolvedStay || !rate.cancellationPolicies?.length) return;
      return resolveCancellationDeadline({
        cancellationPolicies: rate.cancellationPolicies,
        checkInDate: resolvedStay.checkInDate,
        checkOutDate: resolvedStay.checkOutDate,
      });
    },

    /**
     * The fuller explanation beneath that title: the room-enrichment agent's prose when it exists (the
     * friendliest phrasing), else the deterministic deadline + penalty summary of the structured GDS
     * terms. `undefined` when the rate carries neither, so callers can drop the line.
     *
     * NOT the binding text on a commit surface — hotel checkout keeps using
     * `checkout-policy-utils`/`describeCancellationPolicy`, which puts the structured terms first,
     * because an LLM paraphrase can soften or omit a penalty (ELARA-829 R1).
     */
    get cancellationPolicyDescription(): string | undefined {
      const generated = rate.generated?.cancellationPolicy?.trim();
      if (generated) return generated;
      const summarized = summarizeCancelPolicy(rate.cancellationPolicies, resolvedStay?.checkInDate);
      return summarized.length > 0 ? summarized.join(' · ') : undefined;
    },

    get mealCombinationId(): MealCombinationId {
      return deriveMealCombinationId(
        {
          ...rate,
          breakfastIncluded: this.breakfastIncluded,
          lunchIncluded: this.lunchIncluded,
          dinnerIncluded: this.dinnerIncluded,
        },
        MEAL_COMBINATIONS,
      );
    },

    get hasMeals(): boolean {
      return this.breakfastIncluded || this.lunchIncluded || this.dinnerIncluded;
    },

    get mealsIncluded(): Array<'breakfast' | 'lunch' | 'dinner'> {
      const meals: Array<'breakfast' | 'lunch' | 'dinner'> = [];
      if (this.breakfastIncluded) meals.push('breakfast');
      if (this.lunchIncluded) meals.push('lunch');
      if (this.dinnerIncluded) meals.push('dinner');
      return meals;
    },

    get priceHistory() {
      return rate.priceHistory ?? [];
    },

    matchesMealCombination(combinationId: MealCombinationId): boolean {
      const combination = MEAL_COMBINATIONS.find((combo) => combo.id === combinationId);
      if (!combination) return false;
      return matchesMealCombination(
        {
          breakfastIncluded: this.breakfastIncluded,
          lunchIncluded: this.lunchIncluded,
          dinnerIncluded: this.dinnerIncluded,
        },
        combination,
      );
    },
  };
});

// Public API
export type RoomRateModel = ReturnType<(typeof RoomRateModelFactory)['from']>;

export function createRoomRateModel(rate: RoomRateInput, stay?: RoomRateStay): RoomRateModel {
  return RoomRateModelFactory.from(rate, stay);
}
